import * as React from "react";
import { MediaPlayer } from "@/components";
import { Card, Avatar, PaginationSimple, Badge, Button } from "@/components/ui";
import { SparklesIcon } from "lucide-react";
import moment from "moment-timezone";
import { promptSpecificDate, useAccountSettings, useSocialCoachSession } from "@/utils";
import { SocialMentionBadge } from "@/components/SocialMentionSearch/components/SocialMentionBadge";
import { usePostFlowContext } from "../SharedPostFlowContext";
import { WebSettings } from "@/interfaces";

interface Props {
  onMentionsClick: () => void;
}

export function ReviewStep({ onMentionsClick }: Props) {
  const { isPlayer, fullName } = useSocialCoachSession();
  const {
    message,
    hashtags,
    addShortLink,
    addDisclaimer,
    media,
    deliveryOptions,
    selectedPlayerIds,
    mentions,
    removeMention,
    effectiveTotalPostCount,
  } = usePostFlowContext();

  const [currentMediaPage, setCurrentMediaPage] = React.useState(1);
  const totalMedia = media.length;
  const currentMedia = media[currentMediaPage - 1];
  const { hasAccountSetting } = useAccountSettings();
  const hasMentionsFeature = hasAccountSetting(WebSettings.MENTIONS);

  const when =
    deliveryOptions.sendMode === "SCHEDULED" && deliveryOptions.date && deliveryOptions.time
      ? moment(promptSpecificDate(deliveryOptions.date, deliveryOptions.time)).format("MM/DD/YY @ h:mm a")
      : "Immediately";

  return (
    <div className="flex flex-col gap-6">
      <div
        className={`grid grid-cols-1 ${
          hasMentionsFeature ? "lg:grid-cols-[1fr_320px]" : ""
        } gap-6 max-w-6xl mx-auto w-full`}
      >
        <Card className="gap-0 pt-0 overflow-hidden">
          <Card.Header className="flex flex-row items-center justify-between gap-3 py-3 bg-white z-10 border-none">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar>
                <Avatar.Fallback className="text-xs font-bold bg-primary/10 text-primary">
                  {effectiveTotalPostCount}
                </Avatar.Fallback>
              </Avatar>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold truncate">
                  {isPlayer
                    ? fullName
                    : selectedPlayerIds.length === 1
                    ? "1 Player"
                    : `${selectedPlayerIds.length} Players`}
                </span>
                <span className="text-[12px] text-muted-foreground leading-none">
                  {deliveryOptions.isAutoPost
                    ? `${deliveryOptions.socialNetworks.length} Networks`
                    : "Push Notification"}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end text-right shrink-0">
              <span className="text-sm font-semibold">Scheduled</span>
              <span className="text-[12px] text-muted-foreground leading-none">
                {deliveryOptions.sendMode === "SCHEDULED" ? when : "Immediately"}
              </span>
            </div>
          </Card.Header>

          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[300px]">
            {/* Left Side: Media */}
            <div className="flex flex-col bg-slate-50 rounded-lg overflow-hidden m-2">
              <div className="flex-1 flex items-center justify-center overflow-hidden rounded-lg max-h-[350px]">
                {currentMedia ? (
                  <div className="reviewMediaSection w-full h-full flex items-center justify-center">
                    <div className="postMediaContainer w-full">
                      <MediaPlayer media={currentMedia} previewOnly={true} />
                    </div>
                  </div>
                ) : (
                  <div className="py-20 flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="text-sm italic">No media attached</span>
                  </div>
                )}
              </div>

              {totalMedia > 1 && (
                <div className="pb-4 px-2">
                  <PaginationSimple
                    page={currentMediaPage}
                    totalPages={totalMedia}
                    onPageChange={setCurrentMediaPage}
                    hideLabel={true}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            {/* Right Side: Caption & Info */}
            <div className="flex flex-col bg-slate-50 rounded m-2 overflow-y-auto max-h-[350px]">
              <div className="p-6 flex flex-col gap-6 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                {message ? <div>{message}</div> : <div className="text-muted-foreground italic">No caption</div>}

                {mentions.length > 0 && (
                  <div className="text-xs text-slate-400">-- Custom Mentions will be added before posting --</div>
                )}

                {!isPlayer && (
                  <>
                    {addShortLink && <div>https://myoc.io/example-short-link</div>}

                    {hashtags && hashtags.length > 0 && <div className="text-primary font-medium">{hashtags}</div>}

                    {addDisclaimer && (
                      <div className="text-xs text-slate-400">-- Compliance Disclaimer will go here --</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Beta Mentions Info Column */}
        {hasMentionsFeature && (
          <div className="flex flex-col gap-4 pt-0">
            <div className="flex flex-col gap-4 bg-primary/3 border border-primary/10 rounded-2xl p-6 h-full">
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="bg-primary/10 text-primary border-none px-2 py-0 h-5 text-[10px] font-bold uppercase tracking-wider"
                >
                  Beta
                </Badge>
                <div className="flex items-center gap-1.5 text-primary">
                  <SparklesIcon className="size-4" />
                  <span className="text-sm font-bold">Custom Mentions</span>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <p className="text-sm text-slate-600 leading-relaxed text-balance">
                  Tag specific people or pages directly in your players' captions to increase engagement and reach.
                </p>

                {mentions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {mentions.map(mention => (
                      <SocialMentionBadge
                        key={`${mention.socialNetworkId}-${mention.externalId}`}
                        mention={mention}
                        onRemove={() => removeMention(mention)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <div className="size-6 rounded-full bg-white border border-primary/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <span className="text-[10px] font-bold text-primary">1</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-bold text-slate-900">Select Player & Network</span>
                        <p className="text-[11px] text-slate-500 leading-normal">
                          Choose who you want to customize the mention for.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="size-6 rounded-full bg-white border border-primary/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <span className="text-[10px] font-bold text-primary">2</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-bold text-slate-900">Search & Tag</span>
                        <p className="text-[11px] text-slate-500 leading-normal">
                          Search for the exact profile or page you want to mention.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="size-6 rounded-full bg-white border border-primary/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <span className="text-[10px] font-bold text-primary">3</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-bold text-slate-900">Automatic Sync</span>
                        <p className="text-[11px] text-slate-500 leading-normal">
                          We'll automatically format the caption with the correct handle.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={onMentionsClick}
                  className="w-full bg-white hover:bg-slate-50 text-primary border-primary/20 h-10 text-xs font-bold shadow-sm"
                  variant="outline"
                >
                  {mentions.length > 0 ? "Edit Custom Mentions" : "Add Custom Mentions"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
